<script setup lang="ts">
import { ref, onMounted, nextTick, computed, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import ScrollTop from "primevue/scrolltop";
import SplitButton from "primevue/splitbutton";

// Import our new components
import ProductSelectionHeader from "../components/textMaintenance/ProductSelectionHeader.vue";
import LanguageSelectionSection from "../components/textMaintenance/LanguageSelectionSection.vue";
import TabsContainer from "../components/textMaintenance/TabsContainer.vue";
import CSVExportDialog from "../components/textMaintenance/CSVExportDialog.vue";
import CSVImportDialog from "../components/textMaintenance/CSVImportDialog.vue";
import AddIdentifierDialog from "../components/textMaintenance/AddIdentifierDialog.vue";
import ImportSummaryDialog from "../components/textMaintenance/ImportSummaryDialog.vue";
import { useCSVOperations, type ImportType, type ImportResult } from "../composables/useCSVOperations";
import { useNavigationProtection } from "../composables/useNavigationProtection";
import { useTextMaintenanceStore } from "../stores/textMaintenanceStore";
import { storeToRefs } from "pinia";
import router from "../router/router.ts";
import { onBeforeRouteLeave, useRoute } from "vue-router";

const { t } = useI18n();
const toast = useToast();
const route = useRoute();

// CSV export helpers still need toast/i18n, so they stay in the composable.
const { downloadCSV } = useCSVOperations();

const {
    setupNavigationProtection,
    confirmNavigation
} = useNavigationProtection();

// Single source of truth for all text-maintenance state and logic.
const store = useTextMaintenanceStore();
const {
    availableLanguages,
    productOptions,
    loadingProducts,
    selectedProduct,
    activeTabIndex,
    loading,
    hasUnsavedChanges,
    hasLanguageChanges,
    profileMaintenanceData,
    questionnaireMaintenanceData,
    defaultMaintenanceData,
    selectedLanguages,
    hasDefaultTexts,
} = storeToRefs(store);

const {
    loadProducts,
    fetchTextsForProduct,
    processLanguageSelection,
    handleUpdateItem,
    deleteItem,
    addIdentifier,
    addLanguage,
    validateNewIdentifier,
    importCSV,
    saveChanges,
    resetAllChanges,
} = store;

// URL parameter handling functions
function parseLanguagesFromQuery(langParam: string | string[]): string[] {
    const langString = Array.isArray(langParam) ? langParam[0] : langParam;
    if (!langString) return [];

    // Split by comma and take max 2 languages
    return langString.split(',').slice(0, 2).map(lang => lang.trim()).filter(Boolean);
}

async function applyUrlParameters() {
    const productParam = route.query.product as string;
    const languageParam = route.query.language as string | string[];

    if (!productParam) return;

    // Parse product parameter for prefixes
    let actualProduct = productParam;
    let targetTab = 0;

    if (productParam.startsWith('questionnaire-')) {
        actualProduct = productParam.replace('questionnaire-', '');
        targetTab = 1; // Questionnaire tab
    } else if (productParam.startsWith('profile-')) {
        actualProduct = productParam.replace('profile-', '');
    } else {
        // No prefix means it could be a "texts" tab product
        // We'll determine the correct tab after loading the product
        targetTab = -1; // Flag to determine later
    }

    // Apply product parameter
    if (actualProduct && productOptions.value.includes(actualProduct)) {
        await handleProductChange(actualProduct, false); // Don't update URL when applying from URL

        // Determine the correct tab based on available data
        if (targetTab === -1) {
            if (hasDefaultTexts.value) {
                targetTab = 2;
            } else {
                targetTab = 0;
            }
        }

        // Set the appropriate tab after product is loaded
        activeTabIndex.value = targetTab;

        // Apply language parameters after product is loaded
        if (languageParam) {
            const languagesToSelect = parseLanguagesFromQuery(languageParam);
            selectLanguagesFromQuery(languagesToSelect);
        }
    }
}

function selectLanguagesFromQuery(languageCodes: string[]) {
    // Keep only languages that actually exist for the product; let the store
    // drive the selection/disabled state and re-filter.
    const valid = languageCodes.filter(code => availableLanguages.value.some(lang => lang.code === code));
    if (valid.length > 0) {
        processLanguageSelection(valid);
    }
}

// Combined loading state
const isLoading = computed(() => loading.value);

// Check if there's any data available for export (regardless of language selection)
// Also ensure we're not currently loading to prevent premature export attempts
const hasAnyData = computed(() =>
    !loading.value &&
    (store.domains.profile.raw.length > 0 ||
        store.domains.questionnaire.raw.length > 0 ||
        store.domains.default.raw.length > 0)
);

// Dialog state for adding new items
const showAddItemDialog = ref(false);
const newItemTexts = ref<{ [key: string]: string }>({});

// Export dialog state
const showExportDialog = ref(false);

// Error dialog state
const showErrorDialog = ref(false);
const errorDialogTitle = ref('');
const errorDialogMessage = ref('');

// Import summary dialog state
const showImportSummaryDialog = ref(false);
const importSummary = ref<ImportResult | null>(null);

// CSV Import dialog state
const showCSVImportDialog = ref(false);
const csvImportDialogRef = ref<InstanceType<typeof CSVImportDialog>>();

// Helper function to show error dialog
function showError(title: string, message: string) {
    errorDialogTitle.value = title;
    errorDialogMessage.value = message;
    showErrorDialog.value = true;
}

// Setup navigation protection
const cleanupNavigation = setupNavigationProtection(() => store.hasUnsavedChanges);

// Vue Router navigation guard
onBeforeRouteLeave(() => {
    return confirmNavigation(() => store.hasUnsavedChanges);
});

onUnmounted(() => {
    cleanupNavigation();
});

// Helper function to update URL parameters
function updateUrlParameters() {
    const query: Record<string, string> = {};
    if (selectedProduct.value) {
        // Add appropriate prefix based on active tab and product type
        let productParam;

        if (hasDefaultTexts.value) {
            // For products with only default texts, don't add any prefix
            productParam = selectedProduct.value;
        } else if (activeTabIndex.value === 1) {
            // For profile/questionnaire products, add appropriate prefix
            productParam = `questionnaire-${selectedProduct.value}`;
        } else {
            productParam = `profile-${selectedProduct.value}`;
        }

        query.product = productParam;
    }

    // Add language parameter (up to 2 selected languages)
    const selected = selectedLanguages.value.slice(0, 2);
    if (selected.length > 0) {
        query.language = selected.join(',');
    }
    router.replace({ query });
}

// Product change handler
async function handleProductChange(product: string, updateUrl: boolean = true) {
    selectedProduct.value = product;
    availableLanguages.value.length = 0;

    if (product) {
        await nextTick();
        await fetchTextsForProduct(product);
    }

    // Update URL parameters only if requested
    if (updateUrl) {
        updateUrlParameters();
    }
}

// Tab change handler
function handleTabChange(index: number) {
    activeTabIndex.value = index;
    // Update URL parameters when tab changes (for profile/questionnaire products)
    if (!hasDefaultTexts.value) {
        updateUrlParameters();
    }
}

// Language selection handler
async function handleLanguageSelect(selectedLanguageCodes: string[]) {
    await processLanguageSelection(selectedLanguageCodes);
    // Update URL parameters after language selection
    updateUrlParameters();
}

// Handle product creation
async function handleProductCreated(newProductId: string) {
    console.log('Product created:', newProductId);

    await loadProducts();
    await handleProductChange(newProductId, true);
}

// Wrapper functions for store actions (toasts/UI feedback live in the view)
async function handleSaveChanges() {
    const result = await saveChanges();

    if (result.status === "no-selection") {
        toast.add({
            severity: "warn",
            summary: t("textMaintenance.save.noSelection"),
            detail: t("textMaintenance.save.noSelectionDetail"),
            life: 5000,
        });
        return;
    }

    if (result.success) {
        toast.add({
            severity: "success",
            summary: t("textMaintenance.save.successful"),
            detail: t("textMaintenance.save.successfulDetail", { count: result.savedCount }),
            life: 8000,
            closable: true,
        });
    } else {
        toast.add({
            severity: "error",
            summary: t("textMaintenance.save.failed"),
            detail: t("textMaintenance.save.failedDetail", { count: result.errors.length }),
            life: 8000,
        });
        console.error("Save errors:", result.errors);
    }
}

async function handleResetAllChanges() {
    await resetAllChanges();
}

// Wrapper function for item operations
function handleUpdateItemWrapper(updatedItem: any) {
    handleUpdateItem(updatedItem);
}

function handleDeleteItemWrapper(id: string) {
    deleteItem(id, selectedLanguages.value);
}

// Handle adding new identifier
function handleAddItem() {
    console.log("handleAddItem called!");
    console.log("selectedLanguages:", selectedLanguages.value);

    // Open the dialog
    showAddItemDialog.value = true;
    console.log("Dialog should be open now, showAddItemDialog:", showAddItemDialog.value);
}

function confirmAddItem(data: { identifier: string; texts: Record<string, string> }) {
    const { identifier, texts } = data;

    const validation = validateNewIdentifier(identifier);
    if (!validation.valid) {
        console.warn("Validation failed:", validation.message);
        toast.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: validation.message,
            life: 3000
        });
        return;
    }

    const success = addIdentifier(identifier, texts);

    if (success) {
        toast.add({
            severity: 'success',
            summary: 'Item Added Successfully',
            detail: `Identifier "${identifier}" has been added successfully.`,
            life: 3000
        });
        showAddItemDialog.value = false;
    } else {
        toast.add({
            severity: 'error',
            summary: 'Failed to Add Item',
            detail: 'Unable to add the new identifier. Please try again.',
            life: 3000
        });
    }
}

function cancelAddItem() {
    showAddItemDialog.value = false;
    newItemTexts.value = {};
}

// Dialog handlers
function handleAddLanguage(languageData: { code: string; name: string }) {
    const { code, name } = languageData;
    if (code && selectedProduct.value) {
        addLanguage(code, name);
    } else {
        console.warn("Missing required data:", { code, selectedProduct: selectedProduct.value });
    }
}

// CSV Import Dialog Handlers
function showCSVImportDialogHandler() {
    if (!selectedProduct.value) {
        showError(
            t("textMaintenance.importError.error"),
            t("textMaintenance.importError.noProductSelected")
        );
        return;
    }
    showCSVImportDialog.value = true;
}

async function handleCSVImport(data: { file: File; importType: ImportType }) {
    console.log('CSV Import with dialog data:', data);

    try {
        // Set the active tab based on import type
        let targetTabIndex = 0; // Default to profile tab
        if (data.importType === 'questionnaire') {
            targetTabIndex = 1;
        } else if (data.importType === 'texts') {
            targetTabIndex = 2;
        }
        // No need to set for 'profile' as it's already the default

        activeTabIndex.value = targetTabIndex;

        // Perform the actual import with the specified type
        await performCSVImport(data.file, data.importType);

        // Close the dialog on success
        showCSVImportDialog.value = false;
    } catch (error) {
        console.error('CSV import failed:', error);
        // Keep dialog open on error so user can retry
    } finally {
        // Always stop the loading state
        if (csvImportDialogRef.value) {
            csvImportDialogRef.value.setImporting(false);
        }
    }
}

// File handling
async function performCSVImport(file: File, importType: ImportType = 'profile') {
    console.log('performCSVImport called with file:', file.name, 'Type:', file.type, 'Size:', file.size, 'ImportType:', importType);

    if (!selectedProduct.value) {
        console.warn('CSV Import blocked: Missing product', {
            selectedProduct: selectedProduct.value
        });
        showError(
            t("textMaintenance.importError.error"),
            t("textMaintenance.importError.noProductSelected")
        );
        return;
    }

    console.log('CSV Import: Prerequisites met, processing file...');

    try {
        const fileContent = await file.text();

        // The store applies ALL matched languages from the file, independent of the
        // ≤2 languages shown in the editing grid.
        const result = await importCSV(fileContent, importType);

        // Show the full multi-language result so the user sees what changed across
        // every language, not just the two displayed columns.
        importSummary.value = result;
        showImportSummaryDialog.value = true;

        toast.add({
            severity: "success",
            summary: t("textMaintenance.importSuccess"),
            detail: t("textMaintenance.importSuccessDetail", {
                imported: result.imported,
                updated: result.updated || 0,
                skipped: result.skipped
            }),
            life: 6000,
        });
    } catch (error) {
        console.error("CSV import failed:", error);
        showError(
            t("textMaintenance.importError.error"),
            error instanceof Error ? error.message : t("textMaintenance.importError.errorDetail")
        );
    }
}

function performCSVExport() {
    // Open the export dialog instead of immediately exporting
    showExportDialog.value = true;
}

function handleExportConfirm(exportOptions: { languages: string[]; mode: ImportType }) {
    const { languages, mode } = exportOptions;

    const csvContent = store.buildExportContent(languages, mode);

    const productSuffix = selectedProduct.value || 'texts';
    const modeSuffix = `-${mode}`;
    const langSuffix = languages.length > 3 ? `${languages.length}langs` : languages.join('-');
    const filename = `${productSuffix}${modeSuffix}-${langSuffix}.csv`;

    downloadCSV(csvContent, filename);
}

const diffButtonItems = computed(() => {
    const product = selectedProduct.value || "";
    const langs = selectedLanguages.value;

    // no product selected -> no menu
    if (!product) return [];

    const variants = [
        { key: "profile", label: t("textMaintenance.tabs.profile", { product }), prefix: "profile-" },
        { key: "questionnaire", label: t("textMaintenance.tabs.questionnaire", { product }), prefix: "questionnaire-" },
    ];

    const items: any[] = [];

    for (const lang of langs) {
        const subItems = variants.map((variant) => ({
            label: `${variant.label}`,
            icon: variant.key === "profile" ? "pi pi-user" : "pi pi-question",
            command: () => goToTextDiffs(lang, `${variant.prefix}${product}`),
        }));

        items.push({
            label: lang.toUpperCase(),
            icon: "pi pi-language",
            items: subItems, // nested submenu
        });
    }

    return items;
});

function goToTextDiffs(languageOverride?: string, productOverride?: string) {
    let product = productOverride;

    if (!product && selectedProduct.value) {
        // Determine prefix based on active tab
        const prefix = activeTabIndex.value === 1 ? 'questionnaire-' : 'profile-';
        product = `${prefix}${selectedProduct.value}`;
    }

    if (!product) product = "";

    const language = languageOverride || (selectedLanguages.value.length === 1 ? selectedLanguages.value[0] : "");

    if (!product) return;

    router.push({
        path: "/text-diffs",
        query: { product, language },
    });
}

// Initialize on mount
onMounted(async () => {
    await loadProducts();
    // Apply URL parameters after products are loaded
    await applyUrlParameters();
});
</script>

<template>
    <div class="text-maintenance-page">
        <Toast />

        <!-- Page Header -->
        <div class="flex justify-between items-center mb-2">
            <h1 class="page-title">{{ t("textMaintenance.title") }}</h1>

            <!-- Save Controls (only show when there are changes) -->
            <div v-if="hasUnsavedChanges" class="flex items-center gap-2">
                <Button
                    :label="t('textMaintenance.table.resetAllChanges')"
                    icon="pi pi-refresh"
                    severity="warn"
                    @click="handleResetAllChanges"
                    :loading="loading"
                    class="p-button-outlined"
                    :title="t('textMaintenance.table.resetAllChangesTooltip')"
                    data-cy="text-maintenance_reset-all-button"
                />
                <Button
                    :label="t('textMaintenance.saveAllChanges')"
                    icon="pi pi-save"
                    @click="handleSaveChanges"
                    :loading="loading"
                    data-cy="text-maintenance_save-button"
                />
            </div>
            <div class="flex items-center gap-2">
                <div class="flex items-center gap-2">
                    <SplitButton
                        :label="t('textMaintenance.releaseToProd')"
                        icon="pi pi-external-link"
                        :model="diffButtonItems"
                        :disabled="!selectedProduct || selectedLanguages.length === 0"
                        @click="() => goToTextDiffs()"
                        data-cy="text-maintenance_text-diffs-button"
                    />
                </div>
            </div>
        </div>

        <!-- Product Selection Header -->
        <ProductSelectionHeader
            :product-options="productOptions"
            :selected-product="selectedProduct"
            :loading-products="loadingProducts"
            :selected-languages-count="selectedLanguages.length"
            :has-items="hasAnyData"
            :loading="isLoading"
            @product-change="handleProductChange"
            @products-updated="loadProducts"
            @product-created="handleProductCreated"
            @import="showCSVImportDialogHandler"
            @export="performCSVExport"
        />

        <!-- Language Selection Section -->
        <LanguageSelectionSection
            :selected-product="selectedProduct"
            :available-languages="availableLanguages"
            :has-language-changes="hasLanguageChanges"
            :loading="loading"
            :loading-languages="isLoading && availableLanguages.length === 0"
            @language-select="handleLanguageSelect"
            @add-language="handleAddLanguage"
        />

        <!-- Tabs Container -->
        <TabsContainer
            :selected-product="selectedProduct"
            :selected-languages="selectedLanguages"
            :available-languages="availableLanguages"
            :profile-data="profileMaintenanceData"
            :questionnaire-data="questionnaireMaintenanceData"
            :default-data="defaultMaintenanceData"
            :has-default-texts="hasDefaultTexts"
            :active-tab-index="activeTabIndex"
            :loading="loading"
            @tab-change="handleTabChange"
            @delete-item="handleDeleteItemWrapper"
            @update-item="handleUpdateItemWrapper"
            @add-item="handleAddItem"
        />

        <ScrollTop />

        <!-- Add Item Dialog -->
        <AddIdentifierDialog
            v-model:visible="showAddItemDialog"
            :selected-languages="selectedLanguages"
            :loading="loading"
            :validate-identifier="(id) => {
                const result = validateNewIdentifier(id);
                return { valid: result.valid, message: result.message || '' };
            }"
            @confirm="confirmAddItem"
            @cancel="cancelAddItem"
        />

        <!-- Export CSV Dialog -->
        <CSVExportDialog
            v-model:visible="showExportDialog"
            :available-languages="availableLanguages"
            :has-default-texts="hasDefaultTexts"
            @export="handleExportConfirm"
        />

        <!-- Error Dialog -->
        <Dialog
            v-model:visible="showErrorDialog"
            modal
            :header="errorDialogTitle"
            :style="{ width: '450px' }"
            data-cy="text-maintenance_error-dialog"
        >
            <div class="flex items-center gap-4">
                <i class="pi pi-exclamation-triangle text-red-500" style="font-size: 2rem;"></i>
                <span class="text-content">{{ errorDialogMessage }}</span>
            </div>
            <template #footer>
                <Button
                    :label="t('buttons.ok')"
                    @click="showErrorDialog = false"
                    data-cy="text-maintenance_error-dialog-ok"
                />
            </template>
        </Dialog>

        <!-- CSV Import Dialog -->
        <CSVImportDialog
            ref="csvImportDialogRef"
            :visible="showCSVImportDialog"
            :selected-product="selectedProduct"
            :has-default-texts="hasDefaultTexts"
            @update:visible="val => showCSVImportDialog = val"
            @import="handleCSVImport"
        />

        <!-- Import Summary Dialog -->
        <ImportSummaryDialog
            v-model:visible="showImportSummaryDialog"
            :summary="importSummary"
            :available-languages="availableLanguages"
        />
    </div>
</template>

<style scoped>
.text-maintenance-page {
    padding: 1rem;
}

.p-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Programming font for identifier input */
:deep(.identifier-input input) {
    font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", "source-code-pro", monospace;
    font-size: 0.9rem;
}

/* Reset All Button Styling - consistent with MaintenanceTable */
.p-button.p-button-warn.p-button-outlined {
    color: var(--p-orange-600);
    border-color: var(--p-orange-600);
}

.p-button.p-button-warn.p-button-outlined:hover {
    background-color: var(--p-orange-50);
    color: var(--p-orange-700);
    border-color: var(--p-orange-700);
}
</style>
